import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MRT_ColumnFiltersState, MRT_DensityState, MRT_PaginationState, MRT_SortingState, MRT_TableState, MRT_VisibilityState } from 'material-react-table';
import { queryClient } from '@/lib/queryClient';
import { createUserQueryOptions } from '@/services/auth/auth.options';
import { createUserSettingsOptions } from '@/services/setting/setting.options';
import { useWindowSize } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import { useMediaQuery, useTheme } from '@mui/material';
import { z } from 'zod';

interface ICustomFilters {
  customer: {
    id: number,
    label: string
  } | undefined,
  category: {
    id: number,
    label: 'Active' | 'Closed' | 'All' | 'Assigned' | undefined
  } | undefined,
}

// Define the ITicketTableStore interface

// interface ITicketTableStore {
//   showDrawer: boolean;
//   pagination: MRT_PaginationState;
//   sorting: MRT_SortingState;
//   columnFilters: MRT_ColumnFiltersState;
//   columnVisibility: MRT_VisibilityState;
//   density: MRT_DensityState;
//   globalFilter: string | undefined;
//   showColumnFilters: boolean;
//   customFilters: ICustomFilters
//   ticketIds: number[]
//   isFilterVisible: boolean
//   columnOrder: string[]
// }

export const ticketTableStoreSchema = z.object({
  showDrawer: z.boolean(),
  pagination: z.custom<MRT_PaginationState>(),
  sorting: z.custom<MRT_SortingState>(),
  columnFilters: z.custom<MRT_ColumnFiltersState>(),
  columnVisibility: z.custom<MRT_VisibilityState>(),
  density: z.custom<MRT_DensityState>(),
  globalFilter: z.string().optional(),
  showColumnFilters: z.boolean(),
  customFilters: z.object({
    customer: z.object({
      id: z.number(),
      label: z.string()
    }).optional().nullable(),
    category: z.object({
      id: z.number(),
      label: z.enum(['Active', 'Closed', 'All', 'Assigned']).optional()
    }).optional().nullable()
  }),
  ticketIds: z.array(z.number()),
  isFilterVisible: z.boolean(),
  columnOrder: z.array(z.string())
});

type ITicketTableStore = z.infer<typeof ticketTableStoreSchema>;

// Define the Zustand store interface
interface TicketTableStore {
  ticketTableState: ITicketTableStore;
  initializeStore: () => Promise<void>;
  updateTicketTable: (newState: Partial<ITicketTableStore>) => void;
  setPagination: (updater: MRT_PaginationState | ((prev: MRT_PaginationState) => MRT_PaginationState)) => void;
  setSorting: (updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState)) => void;
  setColumnFilters: (updater: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => void;
  setColumnVisibility: (updater: MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState)) => void;
  setDensity: (updater: MRT_DensityState | ((prev: MRT_DensityState) => MRT_DensityState)) => void;
  setGlobalFilter: (updater: string | undefined | ((prev: string | undefined) => string | undefined)) => void;
  setShowColumnFilters: (updater: boolean | ((prev: boolean) => boolean)) => void;
  setCustomFilters: (updater: ICustomFilters | ((prev: ICustomFilters) => ICustomFilters)) => void;
  setTicketIds: (updater: number[] | ((prev: number[]) => number[])) => void;
  setIsFilterVisible: (updater: boolean | ((prev: boolean) => boolean)) => void
  setColumnOrder: (updater: string[] | ((prev: string[]) => string[])) => void;
  reset: () => void
}

const defaultColumnOrder = [
  "TId",
  "Status",
  "Type",
  "conditionalColumn",
  "Priority",
  "Title",
  "Assignee",
  "CustomerName",
  "RaisedBy",
  "DueDate",
  "Created",
  "ClientName",
  "ClientEmail",
  "ClientPhone",
  "ClientAddress",
]


export const getTableColumnSortingState = async (): Promise<any> => {

  try {
    const agentByContext = await queryClient.fetchQuery(createUserQueryOptions({ ticketid: 0, customerid: 0, limittocustomer: false }));

    const getSettings = await queryClient.fetchQuery(createUserSettingsOptions(agentByContext?.ContextUser?.AgentId));

    console.log('getSettings', getSettings)
    const columnSorting = getSettings?.Data?.find(
      (entry: any) => entry.Identifier === 'ticketTableColumnSorting'
    )?.ColumnOrder;

    return columnSorting || defaultColumnOrder
  }
  catch (error) {
    console.log(error)
    return defaultColumnOrder
  }
}

const createInitialState = async (): Promise<ITicketTableStore> => {
  // Hooks can't be used inside regular functions, so we'll need to pass height as a parameter
  const columnOrder = await getTableColumnSortingState()
  const height = window.innerHeight
  const width = window.innerWidth
  // Define breakpoints matching MUI's default breakpoints
  const breakpoints = {
    sm: 600,
    md: 900,
    lg: 1200
  }

  // Determine device type based on width
  const isMobile = height < breakpoints.sm
  const isTablet = height >= breakpoints.sm && height < breakpoints.md
  const isDesktop = height >= breakpoints.lg

  console.log('height', height)
  console.log('columnOrder', columnOrder)

  // Calculate available height for table content
  const getAvailableHeight = () => {
    if (isMobile) {
      console.log('isMobile', isMobile)
      return height - 450 // Mobile has less UI chrome
    } else if (isTablet) {
      console.log('isTablet', isTablet)
      return height - 450 // Tablet has medium UI chrome
    } else {
      console.log('isDesktop', isDesktop)
      return height - 650 // Desktop has full UI chrome
    }
  }

  const availableHeight = getAvailableHeight()
  console.log('availableHeight', availableHeight)

  const calculatePageSize = (availableHeight: number): number => {
    const rowHeight = 18; // Each row is 18px high
    const calculatedRows = Math.floor(availableHeight / rowHeight);
    console.log('calculatedRows', calculatedRows)
    return Math.max(calculatedRows, 1); // Ensure at least 1 row is shown
  };

  const pageSize = calculatePageSize(availableHeight);

  console.log('pageSize', pageSize)

  return {
    showDrawer: false,
    pagination: { pageIndex: 0, pageSize: pageSize },
    sorting: [{ id: 'TId', desc: true }],
    columnFilters: [],
    columnVisibility: { conditionalColumn: false },
    density: 'compact',
    globalFilter: undefined,
    showColumnFilters: true,
    customFilters: { customer: undefined, category: undefined },
    ticketIds: [],
    isFilterVisible: true,
    columnOrder: columnOrder
  }
}

const initialTableState: ITicketTableStore = {
  showDrawer: false,
  // Updated page size to 25.
  pagination: { pageIndex: 0, pageSize: 25 },
  sorting: [{ id: 'TId', desc: true }],
  columnFilters: [],
  columnVisibility: { conditionalColumn: false },
  density: 'compact',
  globalFilter: undefined,
  showColumnFilters: true, //! This was false, which was causing the filters to not be visible
  customFilters: { customer: undefined, category: undefined },
  ticketIds: [],
  isFilterVisible: true,
  columnOrder: defaultColumnOrder
}

// Create the Zustand store with persistence
const useTicketTableStore = create<TicketTableStore>()(
  persist(
    (set) => ({
      ticketTableState: initialTableState,
      initializeStore: async () => {
        const columnOrder = await getTableColumnSortingState();
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            columnOrder
          }
        }));
      },
      updateTicketTable: (newState: Partial<ITicketTableStore>) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            ...newState,
          },
        })),
      setPagination: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            pagination: typeof updater === 'function' ? updater(state.ticketTableState.pagination) : updater,
          }
        })),


      setSorting: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            sorting: typeof updater === 'function' ? updater(state.ticketTableState.sorting) : updater,
          }
        })),

      setColumnFilters: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            columnFilters: typeof updater === 'function' ? updater(state.ticketTableState.columnFilters) : updater,
          }
        })),
      // setColumnFilters: (updater) =>
      //   set((state) => {
      //     // Get the new filters from the updater function or direct value
      //     const newFilters = typeof updater === 'function'
      //       ? updater(state.ticketTableState.columnFilters)
      //       : updater;

      //     // Create a map to store the latest filter for each ID
      //     const filterMap = new Map();

      //     // Process each filter, keeping only the latest one for each ID
      //     newFilters.forEach(filter => {
      //       filterMap.set(filter.id, filter);
      //     });

      //     // Convert the map values back to an array
      //     const uniqueFilters = Array.from(filterMap.values());

      //     return {
      //       ticketTableState: {
      //         ...state.ticketTableState,
      //         columnFilters: uniqueFilters,
      //       }
      //     };
      //   }),

      setColumnVisibility: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            columnVisibility: typeof updater === 'function' ? updater(state.ticketTableState.columnVisibility) : updater,
          }
        })),

      setDensity: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            density: typeof updater === 'function' ? updater(state.ticketTableState.density) : updater,
          }
        })),
      setGlobalFilter: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            globalFilter: typeof updater === 'function' ? updater(state.ticketTableState.globalFilter) : updater,
          }
        })),
      setShowColumnFilters: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            showColumnFilters: typeof updater === 'function' ? updater(state.ticketTableState.showColumnFilters) : updater,
          }
        })),
      setCustomFilters: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            customFilters: typeof updater === 'function' ? updater(state.ticketTableState.customFilters) : updater,
          }
        })),
      setTicketIds: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            ticketIds: typeof updater === 'function' ? updater(state.ticketTableState.ticketIds) : updater,
          }
        })),
      setIsFilterVisible: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            isFilterVisible: typeof updater === 'function' ? updater(state.ticketTableState.isFilterVisible) : updater,
          }
        })),
      setColumnOrder: (updater) =>
        set((state) => ({
          ticketTableState: {
            ...state.ticketTableState,
            columnOrder: typeof updater === 'function' ? updater(state.ticketTableState.columnOrder) : updater,
          }
        })),
      reset: async () => set({ ticketTableState: await createInitialState() }),
    }),
    {
      name: 'ticket-table-storage',
      partialize: (state) => ({
        ticketTableState: {
          pagination: state.ticketTableState.pagination,
          sorting: state.ticketTableState.sorting,
          columnFilters: state.ticketTableState.columnFilters,
          columnVisibility: state.ticketTableState.columnVisibility,
          density: state.ticketTableState.density,
          globalFilter: state.ticketTableState.globalFilter,
          showColumnFilters: state.ticketTableState.showColumnFilters,
          customFilters: state.ticketTableState.customFilters,
          ticketIds: state.ticketTableState.ticketIds,
          columnOrder: state.ticketTableState.columnOrder,
          isFilterVisible: state.ticketTableState.isFilterVisible
        },
      }),
    }
  )
);

export default useTicketTableStore;